import { useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import type { Health, PanelUser } from "../types";

type SetupStatus = {
  needsSetup: boolean;
  username: string;
  /** Super Admin default free-trial length (days) for new installs */
  defaultTrialDays?: number;
  trial: {
    status: string;
    serverIp: string | null;
    expiresAt: string | null;
    daysRemaining: number | null;
    valid: boolean;
    message: string;
    isNewIp?: boolean;
    trialExpired?: boolean;
  } | null;
};

/**
 * First-time installation wizard.
 * Creates the panel owner as username `root` (WHM-style).
 * This is NOT the platform Super Admin — Super Admin manages licenses centrally.
 */
export function SetupPage({
  health,
  onComplete,
  error: externalError
}: {
  health: Health | null;
  onComplete: (payload: {
    token: string;
    user: PanelUser;
    message?: string;
  }) => void;
  error?: string;
}) {
  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const res = await api<SetupStatus & { ok: boolean }>(
          "/api/v1/setup/status"
        );
        setStatus(res);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (password !== password2) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const res = await api<{
        ok: boolean;
        token?: string;
        user?: PanelUser;
        message?: string;
        trial?: { valid?: boolean; message?: string; status?: string };
      }>("/api/v1/setup", {
        method: "POST",
        body: JSON.stringify({
          name,
          email,
          password,
          passwordConfirm: password2
        })
      });
      if (res.token && res.user) {
        onComplete({
          token: res.token,
          user: res.user,
          message: res.message
        });
        return;
      }
      setError(res.message || "Setup completed — log in as root");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="login-page">
        <div className="login-card">
          <p className="lede">Checking installation…</p>
        </div>
      </div>
    );
  }

  const trial = status?.trial;
  const trialExpired = Boolean(trial?.trialExpired);
  const trialOk = Boolean(trial?.valid);

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={(e) => void submit(e)}>
        <div className="brand-mark" aria-hidden>
          <svg viewBox="0 0 64 64" fill="none">
            <path
              d="M14 42V22l10 12 10-12v20"
              stroke="#E8A45C"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="46" cy="24" r="6" stroke="#6EE7B7" strokeWidth="3" />
          </svg>
        </div>
        <h1>Welcome to RealPanel</h1>
        <p className="lede">
          First-time setup. Create the <strong>root</strong> owner account for
          this server (like WHM). This is your panel — not the platform Super
          Admin.
        </p>

        {externalError || error ? (
          <div className="error-banner">{externalError || error}</div>
        ) : null}

        {trial ? (
          <div
            className={
              trialExpired
                ? "error-banner"
                : trialOk
                  ? "success-banner"
                  : "error-banner"
            }
            style={{ textAlign: "left", fontSize: 13 }}
          >
            {trialExpired ? (
              <>
                <strong>Trial expired / blocked</strong>
                <br />
                IP{" "}
                <span className="mono">{trial.serverIp || "—"}</span> already
                used a free trial. After setup you can apply a license key, or
                contact support.
                <br />
                {trial.message}
              </>
            ) : trialOk ? (
              <>
                <strong>
                  {status?.defaultTrialDays || trial.daysRemaining || 7}-day free
                  trial
                </strong>
                {trial.status === "trial" ? " will activate for this IP" : " active"}
                <br />
                Server IP:{" "}
                <span className="mono">{trial.serverIp || "detecting…"}</span>
                {trial.expiresAt
                  ? ` · until ${trial.expiresAt.slice(0, 10)}`
                  : ""}
                {trial.daysRemaining != null
                  ? ` (${trial.daysRemaining}d left)`
                  : ""}
              </>
            ) : (
              <>{trial.message || "License status unknown"}</>
            )}
          </div>
        ) : null}

        <div className="field">
          <label>Username</label>
          <input value="root" disabled readOnly className="mono" />
          <p className="card-meta" style={{ marginTop: 4 }}>
            Fixed as <strong>root</strong> on every RealPanel install (WHM-style).
          </p>
        </div>

        <div className="field">
          <label htmlFor="setup-name">Full name</label>
          <input
            id="setup-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
            autoComplete="name"
            placeholder="Your name"
          />
        </div>

        <div className="field">
          <label htmlFor="setup-email">Email address</label>
          <input
            id="setup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@example.com"
          />
        </div>

        <div className="field">
          <label htmlFor="setup-password">Password</label>
          <input
            id="setup-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>

        <div className="field">
          <label htmlFor="setup-password2">Confirm password</label>
          <input
            id="setup-password2"
            type="password"
            value={password2}
            onChange={(e) => setPassword2(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? "Creating root…" : "Create root account & continue"}
        </button>

        <p className="card-meta" style={{ marginTop: 14 }}>
          {health
            ? `API ${health.status} · ${health.version || ""}`
            : "Connecting…"}
        </p>
      </form>
    </div>
  );
}
